/**
 * Cursor Pagination Component
 *
 * Per Spec 0017 Section 2.4 (Codex Review Issue #3):
 * Facade uses cursor-based pagination. No "Seite X von Y" display
 * since Total only counts items on the current page, not globally.
 *
 * Parent manages a cursorStack[]:
 * - "Next" pushes current cursor onto stack
 * - "Previous" pops from stack
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';

interface CursorPaginationProps {
  hasMore: boolean;
  nextCursor: string | null;
  onNext: (cursor: string) => void;
  onPrevious: () => void;
  hasPrevious: boolean;
  isLoading?: boolean;
}

export function CursorPagination({
  hasMore,
  nextCursor,
  onNext,
  onPrevious,
  hasPrevious,
  isLoading = false,
}: CursorPaginationProps) {
  const { t } = useTranslation('webhook-config');

  const showNext = hasMore && nextCursor;

  if (!hasPrevious && !showNext) {
    return null;
  }

  return (
    <div className="flex items-center justify-between py-4">
      <Button
        variant="outline"
        size="sm"
        onClick={onPrevious}
        disabled={!hasPrevious || isLoading}
      >
        <ChevronLeft className="mr-1 h-4 w-4" />
        {t('pagination.previous', { defaultValue: 'Previous' })}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => nextCursor && onNext(nextCursor)}
        disabled={!showNext || isLoading}
      >
        {t('pagination.next', { defaultValue: 'Next' })}
        <ChevronRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
}
